import { useEffect } from 'react';
import * as signalR from '@microsoft/signalr';

//These types should relect the C# classes in namespace BFG.Domain.SFA.Hubs;
export type SFAHubMessage = {
  messageType: string;
  message: string;
};

export type CrmUpdateMessage = SFAHubMessage & {
  taskAssignedToCompanyUserId: number;
  taskCreatedByUserName: string;
  sfaOrderId: number;
  taskStatus: string;
};

export type SnackAlertMessage = SFAHubMessage & {
  timeoutMs: number;
  severity: 'error' | 'info' | 'success' | 'warning';
};

// Union type for all possible messages
export type HubMessage = CrmUpdateMessage | SnackAlertMessage;

export type UseSignalROptions = {
  companyUserId: number | null;
  onMessageReceived: (message: HubMessage) => void;
};

const baseUrl = import.meta.env.REACT_APP_SERVICE_URL;

// Singleton for managing SignalR connection
const connectionManager = {
  connection: null as signalR.HubConnection | null,
  isStarting: false,
};

export const useSignalR = ({ companyUserId, onMessageReceived }: UseSignalROptions): void => {
  const hubUrl = `${baseUrl}/sfaMessageHub`;

  useEffect(() => {
    console.log('useSignalR', companyUserId, connectionManager.isStarting);

    if (!companyUserId) return;
    if (connectionManager.isStarting) return;

    if (!connectionManager.connection) {
      connectionManager.isStarting = true;

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .build();

      connectionManager.connection = connection;

      connection
        .start()
        .then(() => {
          console.log('SignalR connection established.');
        })
        .catch((err) => {
          console.error('SignalR connection error:', err);
          connectionManager.connection = null;
        })
        .finally(() => {
          connectionManager.isStarting = false;
        });

      // Listen for "ReceiveMessage" and delegate message handling
      connection.on('ReceiveMessage', (message: any) => {
        onMessageReceived(message);
      });
    }

    return () => {
      console.log('useSignalR cleanup');
      if (connectionManager.connection) {
        const connectionToStop = connectionManager.connection;
        connectionManager.connection = null;
        connectionToStop
          .stop()
          .then(() => console.log('SignalR connection stopped.'))
          .catch((err) => console.error('Error stopping SignalR connection:', err));
      }
    };
  }, [hubUrl, companyUserId]);
};
